import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import '../McmvForm.css';
import api from '../../services/api';

interface RegisterFormProps {
  onBackToLogin: () => void;
  onShowHome?: () => void;
  onShowLogin?: () => void;
  onShowRegister?: () => void;
  onShowConsulta?: () => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onBackToLogin, onShowHome, onShowLogin, onShowRegister, onShowConsulta }) => {
  const [formData, setFormData] = useState({
    name: '',
    cpf: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    birth_date: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const [flagsChecked, setFlagsChecked] = useState(false);
  const [inscricaoEnabled, setInscricaoEnabled] = useState(false);

  useEffect(() => {
    const checkInscricaoFlag = async () => {
      try {
        const res = await api.get(`/flags/INSCRICAO`);
        const valor = (res.data?.valor || 'N').toUpperCase();
        setInscricaoEnabled(valor === 'S');
      } catch (e) {
        // Em caso de erro, tratar como desativado para segurança, sem redirecionar
        setInscricaoEnabled(false);
      } finally {
        setFlagsChecked(true);
      }
    };
    checkInscricaoFlag();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (formData.email !== formData.confirmEmail) {
      setError('Os emails não coincidem');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return false;
    }
    if (formData.password.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return false;
    }
    if (formData.cpf.length !== 11) {
      setError('CPF deve ter 11 dígitos');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    const result = await register({
      name: formData.name,
      cpf: formData.cpf,
      email: formData.email,
      password: formData.password,
      birth_date: formData.birth_date
    });
    
    if (!result.success) {
      setError(result.message || 'Erro ao criar conta');
    }
    
    // Sucesso: o usuário será autenticado e redirecionado ao Dashboard.
    // O Dashboard detecta o pós-registro e abre a seção "Ficha MCMV" automaticamente.

    setIsLoading(false);
  };

  const formatCPF = (value: string) => {
    return value.replace(/\D/g, '').slice(0, 11);
  };

  return (
    <div className="mcmv-form-container">
      {!flagsChecked && (
        <div className="form-header">
          <h1>Carregando…</h1>
          <p>Verificando disponibilidade de inscrições</p>
        </div>
      )}
      {flagsChecked && (
        <>
          <div className="landing-header">
            <div className="header-logos-grid">
              <div className="logo-cell left">
                <img
                  src="/images/logo-prefeitura.png"
                  alt="Prefeitura de José de Freitas"
                  className="header-logo"
                />
              </div>
              <div className="logo-cell center">
                <img
                  src="/images/logo-mcmv.png"
                  alt="Programa MCMV"
                  className="header-logo"
                />
              </div>
              <div className="logo-cell right">
                <img
                  src="/images/logo-secretaria.jpg"
                  alt="Secretaria Municipal de Assistência Social e Cidadania"
                  className="header-logo"
                />
              </div>
            </div>
          </div>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={() => (onShowHome ? onShowHome() : onBackToLogin())}>Início</button>
          </div>

          {inscricaoEnabled ? (
            <div className="form-header">
              <h1>Criar Conta</h1>
              <p>Cadastre-se para acessar o sistema MCMV</p>
            </div>
          ) : (
            <div className="form-header">
              <h1>Inscrições indisponíveis</h1>
              <p>As inscrições não estão disponíveis no momento. Tente novamente mais tarde.</p>
            </div>
          )}

          {inscricaoEnabled && (
            <div className="mcmv-form">
              <form onSubmit={handleSubmit} className="form-step">
                <h2>Dados Pessoais</h2>
                
                {error && <div className="error-message">{error}</div>}
                
                <div className="form-group">
                  <label>Nome Completo *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>CPF *</label>
                  <input
                    type="text"
                    name="cpf"
                    value={formData.cpf}
                    onChange={(e) => setFormData(prev => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                    placeholder="Apenas números"
                    maxLength={11}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Confirmar Email *</label>
                  <input
                    type="email"
                    name="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Senha *</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    minLength={8}
                    required
                  />
                  <small className="input-hint">Mínimo de 8 caracteres.</small>
                </div>

                <div className="form-group">
                  <label>Confirmar Senha *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    minLength={8}
                    required
                  />
                  <small className="input-hint">Repita a senha (mínimo 8 caracteres).</small>
                </div>

                <div className="form-group">
                  <label>Data de Nascimento *</label>
                  <input
                    type="date"
                    name="birth_date"
                    value={formData.birth_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-navigation">
                  <button 
                    type="button" 
                    onClick={onBackToLogin}
                    className="btn btn-secondary"
                  >
                    Voltar ao Login
                  </button>
                  <button 
                    type="submit" 
                    disabled={isLoading} 
                    className={`btn btn-primary ${isLoading ? 'btn-loading' : ''}`}
                  >
                    {isLoading ? 'Criando conta...' : 'Criar Conta'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RegisterForm;